import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { Box} from '@mui/material';
import { secondsToTime } from '../../../utils/formatUtils';
import FileUploadIcon from '@mui/icons-material/FileUpload'; // Add this import

const TimelineContainer = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: #1e1e1e;
    color: white;
    
    .timeline-controls {
        display: flex;
        align-items: center;
        padding: 5px;
        background-color: #2c2c2c;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        height: 40px; /* Fixed smaller height */
    }
    
    .timeline-slider {
        flex: 1;
        margin: 0 10px; /* Reduced margins */
    }
    
    .timeline-content {
        flex: 1;
        overflow: auto;
        position: relative;
    }
    
    .timeline-ruler {
        height: 30px;
        position: sticky;
        top: 0;
        z-index: 10;
        background-color: #2c2c2c;
        display: flex;
        border-bottom: 1px solid rgba(255, 255, 255, 0.3);
    }
    
    .timeline-ruler-mark {
        position: absolute;
        height: 30px;
        display: flex;
        flex-direction: column;
        align-items: center;
        font-size: 10px;
        color: rgba(255, 255, 255, 0.7);
    }
    
    .timeline-ruler-mark-line {
        width: 1px;
        height: 10px; /* Increased from 8px */
        background-color: rgba(255, 255, 255, 0.5); /* Increased opacity for visibility */
    }
    
    /* Add major timeline markers that are more visible */
    .timeline-ruler-mark.major .timeline-ruler-mark-line {
        height: 15px;
        width: 2px;
        background-color: rgba(255, 255, 255, 0.8);
    }
    
    .timeline-ruler-mark-text {
        margin-top: 2px;
        font-weight: bold; /* Make text more visible */
    }
    
    .timeline-rows {
        position: relative;
    }
    
    .timeline-row {
        height: 120px; /* Reduced from 150px */
        margin: 15px 0; /* Reduced from 20px */
        position: relative;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    .timeline-row-label {
        position: absolute;
        left: 0;
        top: 0;
        width: 100px; /* Reduced from 120px */
        height: 120px; /* Match row height */
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background-color: #2c2c2c;
        z-index: 5;
        font-weight: bold;
        padding: 0 3px; /* Reduced padding */
        text-align: center;
        border-right: 2px solid;
        border-left: 4px solid;
        font-size: 0.9rem; /* Smaller font */
    }
    
    .timeline-subtitles {
        position: absolute;
        left: 100px; /* Reduced from 120px */
        right: 0;
        top: 0;
        height: 120px; /* Match row height */
    }
    
    /* Update the timeline-subtitle style */
    .timeline-subtitle {
        position: absolute;
        top: 5px; /* Reduced from 10px */
        height: 110px; /* Reduced from 130px */
        background-color: rgba(20, 20, 20, 0.7);
        border-radius: 3px; /* Smaller border radius */
        border-width: 1px; /* Thinner borders except left border */
        border-left-width: 3px; /* Keep left border prominent */
        border-style: solid;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        font-size: 13px; /* Smaller font */
        padding: 3px 6px; /* Less padding */
        overflow: hidden;
        cursor: pointer;
        transition: all 0.2s;
    }
    
    .timeline-subtitle-time {
        font-size: 10px; /* Smaller font */
        background-color: rgba(0, 0, 0, 0.7);
        border-radius: 2px; /* Smaller border radius */
        padding: 1px 3px; /* Less padding */
        width: 100%;
        text-align: center;
        color: #ffffff;
        font-family: monospace;
        z-index: 2;
    }
    
    .timeline-subtitle-text {
        flex: 1;
        width: 100%;
        overflow-y: auto;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 3px 0; /* Less padding */
        text-align: center;
        line-height: 1.3; /* Tighter line height */
        margin: 3px 0; /* Less margin */
        scrollbar-width: thin;
        scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
    }
    
    .timeline-subtitle-text::-webkit-scrollbar {
        width: 4px;
    }
    
    .timeline-subtitle-text::-webkit-scrollbar-track {
        background: transparent;
    }
    
    .timeline-subtitle-text::-webkit-scrollbar-thumb {
        background-color: rgba(255, 255, 255, 0.3);
        border-radius: 4px;
    }
    
    /* Update hover effect */
    .timeline-subtitle:hover {
        background-color: rgba(30, 30, 30, 0.9);
        border-width: 2px; /* Slightly thicker on hover but not too much */
        border-left-width: 3px; /* Keep left border consistent */
        z-index: 30;
        height: auto;
        min-height: 110px; /* Match the default height */
        max-height: 250px; /* Reduced maximum expansion */
        box-shadow: 0 0 6px rgba(255, 255, 255, 0.3); /* Smaller shadow */
    }
    
    /* Update current subtitle style */
    .timeline-subtitle.current {
        background-color: rgba(40, 40, 40, 0.95) !important; /* Darker background */
        border-width: 3px !important; /* Thicker border */
        z-index: 25; /* Higher than normal but lower than hover */
        box-shadow: 0 0 10px rgba(255, 255, 255, 0.7) !important; /* More prominent glow */
        transform: scale(1.05); /* Slightly larger */
        font-weight: bold;
    }

    /* Add a pulsing effect for the current subtitle */
    @keyframes currentPulse {
        0% { box-shadow: 0 0 5px rgba(255, 255, 255, 0.7); }
        50% { box-shadow: 0 0 15px rgba(255, 255, 255, 0.9); }
        100% { box-shadow: 0 0 5px rgba(255, 255, 255, 0.7); }
    }

    .timeline-subtitle.current {
        animation: currentPulse 2s infinite;
    }

    /* Make current subtitle text more visible */
    .timeline-subtitle.current .timeline-subtitle-text {
        color: white;
        font-weight: bold;
    }

    /* Add a visual indicator inside current subtitle */
    .timeline-subtitle.current::after {
        content: '▶';
        position: absolute;
        left: 8px;
        top: 50%;
        transform: translateY(-50%);
        color: white;
        font-size: 12px;
        text-shadow: 0 0 3px rgba(0, 0, 0, 0.8);
    }

    .timeline-grid-line {
        position: absolute;
        top: 30px; /* Start below the ruler */
        bottom: 0;
        width: 1px;
        background-color: rgba(255, 255, 255, 0.1);
        pointer-events: none;
        z-index: 1;
    }

    .timeline-grid-line.major {
        background-color: rgba(255, 255, 255, 0.2);
    }

    .timeline-playhead {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 2px;
        background-color: #ff3030; /* Bright red for high visibility */
        z-index: 20;
        pointer-events: none;
    }

    /* Add a playhead circle at the top for better visibility */
    .timeline-playhead::before {
        content: '';
        position: absolute;
        top: 30px; /* Position it just below the ruler */
        left: -4px;
        width: 10px;
        height: 10px;
        background-color: #ff3030;
        border-radius: 50%;
        box-shadow: 0 0 5px rgba(255, 48, 48, 0.8);
    }

    /* Add time indicator above playhead */
    .timeline-playhead::after {
        content: attr(data-time);
        position: absolute;
        top: 10px;
        left: -25px;
        width: 50px;
        text-align: center;
        font-size: 11px;
        font-weight: bold;
        color: #ff3030;
        background-color: rgba(0, 0, 0, 0.7);
        padding: 2px 4px;
        border-radius: 3px;
    }
`;

const columnColors = [
    'rgba(76, 175, 80, 0.6)', // Light green
    'rgba(33, 150, 243, 0.6)', // Light blue
    'rgba(255, 152, 0, 0.6)', // Light orange
    'rgba(156, 39, 176, 0.6)', // Light purple
    'rgba(233, 30, 99, 0.6)'   // Light pink
];

// Use columnIndex to determine color when rendering subtitles in timeline
const getSubtitleColor = (columnIndex) => {
    return columnIndex >= 0 && columnIndex < columnColors.length 
        ? columnColors[columnIndex] 
        : 'rgba(76, 154, 255, 0.6)'; // Default light blue color
};

export default function MultiColumnTimeline({
    columns,
    currentTime,
    currentIndex,
    player,
    handleSetTime
}) {
    const timelineRef = useRef(null);
    const [zoom, setZoom] = useState(200); // pixels per second
    const [viewStart, setViewStart] = useState(0); // start time in seconds
    const [duration, setDuration] = useState(0);
    const [rulerMarks, setRulerMarks] = useState([]);
    const labelWidth = 100; // Width of the label area in pixels
    // Update the duration when the player is ready
    useEffect(() => {
        if (player && player.element) {
            setDuration(player.element.duration || 0);
        }
    }, [player]);
    
    // Ensure current time is visible in the timeline
    useEffect(() => {
        if (timelineRef.current && currentTime > 0) {
            const timelineRect = timelineRef.current.getBoundingClientRect();
            const currentPos = currentTime * zoom + 120; // 120px offset for labels
            
            const viewWidth = timelineRect.width;
            const viewStartPx = viewStart * zoom;
            
            // If current position is outside visible area, adjust view
            if (currentPos < viewStartPx + 200 || currentPos > viewStartPx + viewWidth - 200) {
                setViewStart(Math.max(0, currentTime - (viewWidth / zoom) / 2));
            }
        }
    }, [currentTime, zoom, viewStart]);
    
    // Update the useEffect that generates ruler marks
    useEffect(() => {
        if (duration > 0) {
            const marks = [];
            
            // Determine interval based on zoom level
            let interval = 60; // Default: 1 minute interval
            if (zoom >= 150) interval = 5; // 5 second interval at very high zoom
            else if (zoom >= 100) interval = 10; // 10 second interval at high zoom
            else if (zoom >= 50) interval = 30; // 30 second interval at medium zoom
            
            // Generate marks based on the interval
            for (let i = 0; i <= Math.ceil(duration); i += interval) {
                const isMajor = i % 60 === 0; // Major mark every minute
                
                marks.push({
                    time: i,
                    position: i * zoom + 100, // Updated to use labelWidth
                    label: secondsToTime(i),
                    isMajor
                });
                
                // Add minor marks between major intervals at higher zoom levels
                if (zoom >= 50 && interval > 10) {
                    const minorInterval = interval / 2;
                    const minorTime = i + minorInterval;
                    if (minorTime <= duration) {
                        marks.push({
                            time: minorTime,
                            position: minorTime * zoom + 100, // Updated to use labelWidth
                            label: secondsToTime(minorTime),
                            isMajor: false
                        });
                    }
                }
            }
            
            setRulerMarks(marks);
        }
    }, [duration, zoom]);
    
    const handleTimelineClick = (e) => {
        if (timelineRef.current && player) {
            const rect = timelineRef.current.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            
            // Calculate time from x position (accounting for label width)
            if (clickX > 100) {
                const clickTime = (clickX - 100) / zoom + viewStart;
                handleSetTime(Math.max(0, Math.min(clickTime, duration)));
            }
        }
    };
    
    return (
        <TimelineContainer>
            
            
            <Box 
                className="timeline-content" 
                ref={timelineRef} 
                onClick={handleTimelineClick}
                style={{ overflowX: 'auto' }}
            >
                <div 
                    className="timeline-ruler"
                    style={{ width: `${duration * zoom + labelWidth}px` }}
                >
                    {rulerMarks.map((mark, i) => (
                        <div 
                            key={i} 
                            className={`timeline-ruler-mark ${mark.isMajor ? 'major' : 'minor'}`}
                            style={{ left: `${mark.position}px` }}
                        >
                            <div className="timeline-ruler-mark-line"></div>
                            {/* Only show text on major marks or at high zoom */}
                            {(mark.isMajor || zoom >= 50) && (
                                <div className="timeline-ruler-mark-text">{mark.label}</div>
                            )}
                        </div>
                    ))}
                </div>
                
                <div 
                    className="timeline-rows"
                    style={{ width: `${Math.max(duration * zoom + labelWidth, 1000)}px` }}
                >
                    {columns.map((column, rowIndex) => (
                        column.language && (
                            <div key={rowIndex} className="timeline-row">
                                <div className="timeline-row-label" style={{ 
                                    borderRight: `2px solid ${getSubtitleColor(rowIndex)}`,
                                    borderLeft: `4px solid ${getSubtitleColor(rowIndex)}` 
                                }}>
                                    <span style={{ color: getSubtitleColor(rowIndex) }}>
                                        {column.language}
                                    </span>
                                    {column.version !== 'latest' && (
                                        <span style={{ 
                                            fontSize: '10px', 
                                            display: 'block', 
                                            marginTop: '5px',
                                            color: 'rgba(255, 255, 255, 0.7)' // Keep this lighter
                                        }}>
                                            (V{column.versions.find(v => v.id === column.version)?.versionNumber || 'File'})
                                        </span>
                                    )}
                                </div>
                                
                                <div className="timeline-subtitles">
                                    {column.subtitles.map((sub, i) => {
                                        // Calculate width and ensure minimum width for readability
                                        const width = Math.max((sub.endTime - sub.startTime) * zoom, 80);
                                        
                                        // Check if this subtitle is currently playing
                                        const isCurrentSub = currentTime >= sub.startTime && currentTime <= sub.endTime;
                                        
                                        // Check if this column has imported subtitles
                                        const isImported = column.version === 'imported';
                                        
                                        return (
                                            <div
                                                key={i}
                                                className={`timeline-subtitle ${isCurrentSub ? 'current' : ''}`}
                                                style={{
                                                    left: `${(sub.startTime - viewStart) * zoom}px`,
                                                    width: `${width}px`,
                                                    borderColor: getSubtitleColor(rowIndex), // Use color for border instead of background
                                                    borderLeftWidth: '4px', // Thicker left border for visual emphasis
                                                    // Add special styling for imported subtitles
                                                    ...(isImported && {
                                                        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(255,255,255,0.2) 5px, rgba(255,255,255,0.2) 10px)',
                                                        border: '1px solid white'
                                                    })
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSetTime(sub.startTime);
                                                }}
                                                title={`${secondsToTime(sub.startTime)} - ${secondsToTime(sub.endTime)}: ${sub.text2}`}
                                            >
                                                <div className="timeline-subtitle-time">
                                                    {secondsToTime(sub.startTime)}
                                                </div>
                                                <div className="timeline-subtitle-text">
                                                    {sub.text2}
                                                </div>
                                                <div className="timeline-subtitle-time">
                                                    {secondsToTime(sub.endTime)}
                                                </div>
                                                {/* Optional: Add a small indicator icon for imported items */}
                                                {isImported && width > 3 && (
                                                    <FileUploadIcon 
                                                        style={{
                                                            fontSize: '0.6rem',
                                                            position: 'absolute',
                                                            top: '2px',
                                                            right: '2px',
                                                            color: 'white'
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )
                    ))}
                </div>
                
                {/* Playhead indicator showing current time */}
                <div 
                    className="timeline-playhead"
                    style={{ left: `${(currentTime - viewStart) * zoom + labelWidth}px` }}
                    data-time={secondsToTime(currentTime)}
                ></div>
                
                {/* Add vertical grid lines */}
                {rulerMarks.map((mark, i) => (
                    <div 
                        key={`grid-${i}`}
                        className={`timeline-grid-line ${mark.isMajor ? 'major' : ''}`}
                        style={{ left: `${mark.position}px` }}
                    />
                ))}
            </Box>
        </TimelineContainer>
    );
}